import { useState } from 'react'
import { PopupButton } from 'react-calendly'
import { cn } from '@/lib/utils'
import { ContactFormModal } from './ContactFormModal'
import { useCalendlyRoot } from '../hooks/useCalendlyRoot'
import oscarHealthLogo from '/images/carriers/oscar-health.svg'
import unitedHealthcareLogo from '/images/carriers/unitedhealthcare.svg'
import kaiserPermanenteLogo from '/images/carriers/kaiser-permanente.svg'
import blueCrossBlueShieldLogo from '/images/carriers/blue-cross-blue-shield.svg'
import heroProductPreview from '/images/hero-product-preview.png'

const CALENDLY_URL = 'https://calendly.com/benepicks/demo'

const carriers = [
  { name: 'Oscar Health', src: oscarHealthLogo },
  { name: 'UnitedHealthcare', src: unitedHealthcareLogo },
  { name: 'Kaiser Permanente', src: kaiserPermanenteLogo },
  { name: 'Blue Cross Blue Shield', src: blueCrossBlueShieldLogo },
]

const DOTTED_GRID_STYLE = {
  backgroundImage:
    'radial-gradient(circle, rgb(212 212 212) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
  maskImage:
    'radial-gradient(ellipse 60% 50% at 50% 40%, black 40%, transparent 100%)',
  WebkitMaskImage:
    'radial-gradient(ellipse 60% 50% at 50% 40%, black 40%, transparent 100%)',
}

export const Hero = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const rootElement = useCalendlyRoot()

  return (
    <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-20 sm:pb-28 lg:pb-36 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={DOTTED_GRID_STYLE}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16 sm:mb-20 lg:mb-24">
          <div className="text-center lg:text-left">
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-semibold tracking-tight text-neutral-900 mb-6 sm:mb-8 leading-[1.1] sm:leading-[1.08] motion-safe:opacity-0 motion-safe:animate-rise"
              style={{ animationDelay: '0ms' }}
            >
              Health benefits employees{' '}
              <span className="italic font-normal text-neutral-700">
                actually
              </span>{' '}
              use.
            </h1>

            <p
              className="text-base sm:text-lg md:text-xl text-neutral-500 mb-10 sm:mb-12 max-w-2xl mx-auto lg:mx-0 leading-relaxed motion-safe:opacity-0 motion-safe:animate-rise"
              style={{ animationDelay: '100ms' }}
            >
              Stop overpaying for group plans. Sync your payroll, set a budget,
              and let your team choose the insurance they actually want.
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 motion-safe:opacity-0 motion-safe:animate-rise"
              style={{ animationDelay: '200ms' }}
            >
              {rootElement && (
                <PopupButton
                  url={CALENDLY_URL}
                  text="Get a Free Quote →"
                  rootElement={rootElement}
                  className={cn(
                    'w-full sm:w-auto',
                    'bg-orange-primary text-white',
                    'px-7 sm:px-8 py-4 rounded-full',
                    'flex items-center justify-center gap-2.5',
                    'hover:bg-orange-hover',
                    'hover:-translate-y-0.5',
                    'transition-all duration-200',
                    'active:translate-y-0',
                    'text-base sm:text-[17px] font-semibold'
                  )}
                />
              )}
              <button
                onClick={() => setContactModalOpen(true)}
                className={cn(
                  'w-full sm:w-auto',
                  'bg-white text-neutral-700',
                  'px-7 sm:px-8 py-4 rounded-full',
                  'flex items-center justify-center',
                  'border border-neutral-200',
                  'shadow-sm',
                  'hover:-translate-y-0.5',
                  'transition-all duration-200',
                  'active:translate-y-0',
                  'text-base sm:text-[17px] font-semibold',
                  'will-change-transform'
                )}
              >
                Contact Us
              </button>
            </div>
          </div>

          <div
            className="relative mt-4 lg:mt-0 motion-safe:opacity-0 motion-safe:animate-rise"
            style={{ animationDelay: '300ms' }}
          >
            <div
              aria-hidden="true"
              className="absolute -inset-8 bg-gradient-to-br from-orange-200/40 via-orange-100/30 to-transparent blur-3xl rounded-full"
            />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-neutral-900/10 border border-neutral-200/80 bg-white transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(248,124,67,0.25)]">
              <img
                src={heroProductPreview}
                alt="Benepicks strategies comparison table showing annual savings across plan options for Mountain View Medical"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        <ContactFormModal
          open={contactModalOpen}
          onOpenChange={setContactModalOpen}
        />

        <div
          className="flex flex-col items-center gap-5 sm:gap-6 motion-safe:opacity-0 motion-safe:animate-rise"
          style={{ animationDelay: '400ms' }}
        >
          <span className="text-xs sm:text-sm font-normal text-neutral-500 max-w-xl px-4 sm:px-0 text-center">
            Get your employees access today to the insurance plans they need:
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:gap-x-14">
            {carriers.map((carrier) => (
              <img
                key={carrier.name}
                src={carrier.src}
                alt={carrier.name}
                loading="lazy"
                decoding="async"
                className="h-7 sm:h-8 w-auto max-w-[140px] object-contain grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition duration-300"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
